import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, X, Sparkles, Paperclip, Scale } from "lucide-react";
import type { WorksPermitAnalysis, DecisionEvaluation } from "@costadorada/shared";
import { api } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { AttachmentField } from "@/components/AttachmentField";
import type { TypedAttachment } from "@/components/TypedAttachmentField";
import { AiAnalysisPanel } from "@/components/AiAnalysisPanel";
import { DecisionEvaluationPanel } from "@/components/DecisionEvaluationPanel";
import { RulePanel } from "@/components/RulePanel";

// The works-permit review (approve / deny), shared by Solicitudes de Obra and
// Resoluciones: the same notes, AI analysis and decision audit wherever the
// Administration or the Junta decides.

export const PERMIT_FOLDER = "costadorada1/permits";

export interface WorksPermit {
  id: string;
  unitId: string;
  description: string;
  workType: string;
  startDate: string;
  estimatedEndDate: string;
  contractorName?: string | null;
  contractorPhone?: string | null;
  applicantName?: string | null;
  applicantPhone?: string | null;
  applicantPhone2?: string | null;
  applicantEmail?: string | null;
  applicantEmail2?: string | null;
  contractorCompany?: string | null;
  contractorPhone2?: string | null;
  contractorEmail?: string | null;
  contractorEmail2?: string | null;
  plansDelivered?: boolean;
  rulesAcceptedAt?: string | null;
  rulesAcceptedName?: string | null;
  attachments?: Array<TypedAttachment & { id: string }>;
  status: "SUBMITTED" | "UNDER_REVIEW" | "APPROVED" | "REJECTED" | "COMPLETED";
  reviewNotes?: string | null;
  attachmentUrls: string[];
  resolutionFileUrl?: string | null;
  enteredOnBehalf?: boolean;
  aiAnalysis?: WorksPermitAnalysis | null;
  aiFlagged?: boolean;
  aiCheckStartedAt?: string | null;
  aiAnalyzedAt?: string | null;
  aiFlagClearedNote?: string | null;
  aiFlagClearedAt?: string | null;
  aiDecisionEval?: DecisionEvaluation | null;
  decisionEvalDocUrls?: string[];
  submittedAt: string;
  reviewedAt?: string | null;
  unit: { unitNumber: string; ownerName: string };
  submittedBy: { firstName: string; lastName: string };
  reviewedBy?: { firstName: string; lastName: string } | null;
  /** The resolution deciding the permit, with its latest signed copy if one was uploaded. */
  resolution?: {
    id: string;
    status: "DRAFT" | "VOTING" | "APPROVED" | "REJECTED";
    attachments: Array<{ id: string; title: string; fileUrl: string; createdAt: string }>;
  } | null;
}

export const fileName = (url: string) => {
  try {
    return decodeURIComponent(new URL(url).pathname.split("/").pop() || url);
  } catch {
    return url;
  }
};

/** Attachments as the API returns them → the form's editable rows. */
export const toRows = (p: WorksPermit): TypedAttachment[] =>
  p.attachments && p.attachments.length
    ? p.attachments.map(({ kind, title, fileUrl }) => ({ kind, title, fileUrl }))
    : (p.attachmentUrls ?? []).map((u) => ({ kind: "OTRO" as const, title: fileName(u), fileUrl: u }));

// Reusable impartial-decision audit: upload supporting docs + run/re-run the evaluation.
export function DecisionAuditSection({ permit, onDone }: { permit: WorksPermit; onDone: () => void }) {
  const { t } = useTranslation();
  const [evaluation, setEvaluation] = useState<DecisionEvaluation | null>(null);
  const [docUrls, setDocUrls] = useState<string[]>([]);

  useEffect(() => {
    setEvaluation((permit.aiDecisionEval as DecisionEvaluation | undefined) ?? null);
    setDocUrls(permit.decisionEvalDocUrls ?? []);
  }, [permit.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/works-permits/${permit.id}/evaluate-decision`, { additionalDocUrls: docUrls.filter(Boolean) })).data as WorksPermit,
    onSuccess: (data) => {
      setEvaluation((data.aiDecisionEval as DecisionEvaluation | undefined) ?? null);
      onDone();
    },
  });

  return (
    <div className="space-y-3">
      <AttachmentField
        label={t("worksPermits.evalDocs")}
        folder={PERMIT_FOLDER}
        addLabel={t("worksPermits.addEvalDoc")}
        urls={docUrls}
        onChange={setDocUrls}
      />
      <button type="button" className="btn-secondary text-sm" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
        <Scale size={14} />
        {mutation.isPending ? t("worksPermits.evaluating") : evaluation ? t("worksPermits.reevaluate") : t("worksPermits.evaluateDecision")}
      </button>
      {mutation.isError && <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>}
      {evaluation && <DecisionEvaluationPanel evaluation={evaluation} />}
    </div>
  );
}

export function WorksPermitReviewModal({ permit, onClose, onDone }: { permit: WorksPermit | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [notes, setNotes] = useState("");
  const [analysis, setAnalysis] = useState<WorksPermitAnalysis | null>(null);

  useEffect(() => {
    setNotes("");
    setAnalysis((permit?.aiAnalysis as WorksPermitAnalysis | undefined) ?? null);
  }, [permit?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const analyzeMutation = useMutation({
    mutationFn: async () => (await api.post(`/works-permits/${permit!.id}/analyze`)).data as WorksPermit,
    onSuccess: (data) => {
      setAnalysis((data.aiAnalysis as WorksPermitAnalysis | undefined) ?? null);
      onDone();
    },
  });

  const reviewMutation = useMutation({
    mutationFn: async (decision: "APPROVED" | "REJECTED") =>
      (await api.put(`/works-permits/${permit!.id}/review`, { decision, reviewNotes: notes || null })).data,
    onSuccess: () => {
      // The decision also moves the permit's resolution: refresh both screens.
      qc.invalidateQueries({ queryKey: ["works-permits"] });
      qc.invalidateQueries({ queryKey: ["resolutions"] });
      onDone();
      onClose();
      setNotes("");
    },
  });

  function insertAnalysisIntoNotes() {
    if (!analysis) return;
    const parts = [analysis.summaryEs];
    if (analysis.conditions.length) parts.push("Condiciones:\n" + analysis.conditions.map((c) => `• ${c}`).join("\n"));
    setNotes((prev) => (prev ? prev + "\n\n" : "") + parts.join("\n\n"));
  }

  if (!permit) return null;
  return (
    <Modal open={!!permit} onClose={onClose} title={`${t("worksPermits.review")} — ${permit.workType}`} size="lg">
      <div className="space-y-3 text-sm">
        <div className="bg-slate-50 p-3 rounded">
          <p><strong>Unidad:</strong> {permit.unit.unitNumber} · {permit.unit.ownerName}</p>
          <p className="mt-2"><strong>{t("worksPermits.description")}:</strong></p>
          <p className="text-slate-700">{permit.description}</p>
          <p className="mt-2 text-xs text-slate-600">
            {t("worksPermits.sectionApplicant")}: {[permit.applicantName ?? permit.unit.ownerName, permit.applicantPhone, permit.applicantEmail].filter(Boolean).join(" · ")}
            {(permit.contractorName || permit.contractorCompany) && (
              <> · {t("worksPermits.contractorName")}: {[permit.contractorName, permit.contractorCompany, permit.contractorPhone].filter(Boolean).join(" · ")}</>
            )}
            {" · "}{t("worksPermits.plansDeliveredShort")}: {permit.plansDelivered ? t("worksPermits.yes") : t("worksPermits.no")}
          </p>
          {toRows(permit).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {toRows(permit).map((a, i) => (
                <a key={i} href={a.fileUrl} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-xs text-primary hover:underline">
                  <Paperclip size={11} /> <span className="text-slate-500">{t(`worksPermits.docKinds.${a.kind}`)}:</span> {a.title}
                </a>
              ))}
            </div>
          )}
        </div>

        <RulePanel tags={["works-permit", "architectural"]} />

        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-secondary text-sm" onClick={() => analyzeMutation.mutate()} disabled={analyzeMutation.isPending}>
            <Sparkles size={14} />
            {analyzeMutation.isPending ? t("worksPermits.analyzing") : analysis ? t("worksPermits.reanalyze") : t("worksPermits.analyze")}
          </button>
          {analysis && (
            <button type="button" className="text-xs text-primary hover:underline" onClick={insertAnalysisIntoNotes}>
              {t("worksPermits.insertToNotes")}
            </button>
          )}
        </div>
        {analyzeMutation.isError && (
          <div className="text-sm text-red-600">{(analyzeMutation.error as any)?.response?.data?.message ?? t("common.error")}</div>
        )}
        {analysis && <AiAnalysisPanel analysis={analysis} />}

        <div>
          <label className="label">{t("worksPermits.reviewNotes")}</label>
          <textarea className="input min-h-[100px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>

        <div className="border-t border-slate-200 pt-3">
          <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-indigo-900">
            <Scale size={15} /> {t("worksPermits.decisionAudit")}
          </div>
          <p className="mb-2 text-xs text-slate-500">{t("worksPermits.decisionAuditHint")}</p>
          <DecisionAuditSection permit={permit} onDone={onDone} />
        </div>
        {reviewMutation.isError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {(reviewMutation.error as any)?.response?.data?.message ?? t("common.error")}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn bg-red-600 text-white hover:bg-red-700" onClick={() => reviewMutation.mutate("REJECTED")} disabled={reviewMutation.isPending}>
            <X size={14} /> {t("worksPermits.reject")}
          </button>
          <button className="btn bg-green-600 text-white hover:bg-green-700" onClick={() => reviewMutation.mutate("APPROVED")} disabled={reviewMutation.isPending}>
            <Check size={14} /> {t("worksPermits.approve")}
          </button>
        </div>
      </div>
    </Modal>
  );
}
